import { useEffect, useState } from 'react';
import { useAuthHttp } from '@hooks';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { GET_PRODUCT_DETAILS_SUGGESTIONS } from '@constants';
import { CubeIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { ProductSkusFormValues } from './types';
import { VOLUME_UNITS } from './constants';
import { FieldZone } from './FieldZone';
import { ImageField } from './ImageField';

export function ProductSection({
  control,
  formState,
  setValue,
  locked = false,
  collapsed = false,
  onToggleCollapsed,
  onExistingProductSelected,
  showSkuScopedEditNote = false,
}: {
  control: Control<ProductSkusFormValues>;
  formState: UseFormStateReturn<ProductSkusFormValues>;
  setValue: UseFormSetValue<ProductSkusFormValues>;
  locked?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onExistingProductSelected?: () => void;
  showSkuScopedEditNote?: boolean;
}) {
  const authHttp = useAuthHttp();
  const productId = useWatch({ control, name: 'productId' });
  const productName = useWatch({ control, name: 'productName' });
  const productBrand = useWatch({ control, name: 'productBrand' });
  const productCategory = useWatch({ control, name: 'productCategory' });
  const productSubCategory = useWatch({ control, name: 'productSubCategory' });
  const productNetVolume = useWatch({ control, name: 'productNetVolume' });
  const productBarcode = useWatch({ control, name: 'productBarcode' });
  const productDescription = useWatch({ control, name: 'productDescription' });
  const productBarcodeImage = useWatch({
    control,
    name: 'productBarcodeImage',
  });
  const skuProductBarcodeImage = useWatch({
    control,
    name: 'skuProductBarcodeImage',
  });
  const productImage = useWatch({ control, name: 'productImage' });
  const skuProductImage = useWatch({ control, name: 'skuProductImage' });

  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const hasExtraDetails = Boolean(
    productBrand ||
      productCategory ||
      productSubCategory ||
      productNetVolume ||
      productBarcode ||
      productDescription
  );
  const isShowingMoreDetails = showMoreDetails || hasExtraDetails;

  const fetchExistingProducts = async () => {
    try {
      const { data } = await authHttp.post(GET_PRODUCT_DETAILS_SUGGESTIONS, {
        searchQuery: productSearch,
      });
      setProductOptions(
        data.productDetailsSuggestions.map((pd: any) => ({
          ...pd,
          id: pd.productId,
          label: `${pd.name} (${pd.productId})`,
        }))
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    if (productSearch.length >= 4) {
      fetchExistingProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch]);

  if (locked) {
    return (
      <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
        <div className='flex items-center gap-3'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50'>
            <CubeIcon className='h-6 w-6 text-primary' />
          </div>
          <div className='min-w-0'>
            <p className='truncate font-inter text-base font-semibold text-gray-900'>
              Adding SKUs to {productName || productId}
            </p>
            <p className='truncate font-inter text-sm text-gray-500'>
              Product ID: {productId} — every SKU shares these details.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (collapsed) {
    return (
      <section className='rounded-lg border border-gray-200 bg-white shadow-sm'>
        <button
          type='button'
          onClick={onToggleCollapsed}
          className='flex w-full items-center gap-3 p-4 text-left'
          aria-expanded={false}
          aria-label={`Edit product ${productName || productId}`}
        >
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50'>
            <CubeIcon className='h-6 w-6 text-primary' />
          </div>
          <div className='min-w-0 flex-1'>
            <p className='truncate font-inter text-base font-semibold text-gray-900'>
              {productName || productId}
            </p>
            <p className='truncate font-inter text-sm text-gray-500'>
              Product ID: {productId}
            </p>
          </div>
          <span className='flex shrink-0 items-center gap-1 font-inter text-sm font-medium text-primary'>
            Edit
            <ChevronDownIcon className='h-4 w-4' />
          </span>
        </button>
      </section>
    );
  }

  return (
    <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
      <div className='flex flex-col gap-4 lg:max-2xl:flex-row'>
        <div className='min-w-0 flex-1'>
          <FieldZone title='Product details'>
            {showSkuScopedEditNote && (
              <div className='rounded-md bg-amber-50 px-3 py-2 font-inter text-sm text-amber-800'>
                Product changes here are saved with this SKU only — other SKUs
                of this product keep their own product details.
              </div>
            )}
            <Autocomplete
              label='Search existing products'
              options={productOptions}
              value={productSearch}
              onInputChange={setProductSearch}
              onChange={(product) => {
                if (!product) return;
                setValue('productId', product.productId || '', {
                  shouldValidate: true,
                });
                setValue('productName', product.name || '');
                setValue('productDescription', product.description || '');
                setValue('productBrand', product.brand || '');
                setValue('productCategory', product.category || '');
                setValue('productSubCategory', product.subCategory || '');
                setValue('productNetVolume', product.netVolume || '');
                setValue('productNetVolumeUnitOfMeasure', {
                  label: product.netVolumeUnitOfMeasure,
                  value: product.netVolumeUnitOfMeasure,
                });
                setValue('productBarcode', product.barcode?.barcode || '');
                onExistingProductSelected?.();
              }}
              helperText='Pick an existing product to add SKUs to it, or fill in a new one below'
            />
            <div className='flex flex-wrap gap-4 2xl:flex-col'>
              <div className='min-w-[9rem] flex-1'>
                <Controller
                  name='productId'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label='Product ID'
                      placeholder='e.g. COKE-330'
                      isInvalid={!!formState.errors.productId}
                      validationMessage={
                        formState.errors.productId?.message as string
                      }
                    />
                  )}
                />
              </div>
              <div className='min-w-[9rem] flex-1'>
                <Controller
                  name='productName'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label='Product name'
                      placeholder='e.g. Coke Can 330ml'
                      optional
                      isInvalid={!!formState.errors.productName}
                      validationMessage={
                        formState.errors.productName?.message as string
                      }
                    />
                  )}
                />
              </div>
            </div>
            {!isShowingMoreDetails && (
              <div>
                <button
                  type='button'
                  className='text-left text-sm font-medium text-primary hover:underline'
                  onClick={() => setShowMoreDetails(true)}
                >
                  + Add more product details (brand, category, description…)
                </button>
              </div>
            )}
            {isShowingMoreDetails && (
              <>
                <div className='flex flex-wrap gap-4 2xl:flex-col'>
                  <div className='min-w-[8rem] flex-1'>
                    <Controller
                      name='productBrand'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label='Brand'
                          optional
                          isInvalid={!!formState.errors.productBrand}
                          validationMessage={
                            formState.errors.productBrand?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                  <div className='min-w-[8rem] flex-1'>
                    <Controller
                      name='productCategory'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label='Category'
                          optional
                          isInvalid={!!formState.errors.productCategory}
                          validationMessage={
                            formState.errors.productCategory?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                  <div className='min-w-[8rem] flex-1'>
                    <Controller
                      name='productSubCategory'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label='Subcategory'
                          optional
                          isInvalid={!!formState.errors.productSubCategory}
                          validationMessage={
                            formState.errors.productSubCategory
                              ?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='flex flex-wrap gap-4'>
                  <div className='w-40'>
                    <Controller
                      name='productNetVolume'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label='Net Volume'
                          optional
                          isInvalid={!!formState.errors.productNetVolume}
                          validationMessage={
                            formState.errors.productNetVolume?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                  <div className='w-28'>
                    <Controller
                      name='productNetVolumeUnitOfMeasure'
                      control={control}
                      render={({ field }) => (
                        <Select
                          label='Unit'
                          options={VOLUME_UNITS}
                          selected={field.value}
                          setSelected={field.onChange}
                          optional
                          isInvalid={
                            !!formState.errors.productNetVolumeUnitOfMeasure
                          }
                          validationMessage={
                            formState.errors.productNetVolumeUnitOfMeasure
                              ?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                  <div className='min-w-[10rem] flex-1'>
                    <Controller
                      name='productBarcode'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label='Product barcode'
                          placeholder='e.g. 5000112637922'
                          optional
                          isInvalid={!!formState.errors.productBarcode}
                          validationMessage={
                            formState.errors.productBarcode?.message as string
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <Controller
                  name='productDescription'
                  control={control}
                  render={({ field }) => (
                    <Textarea {...field} label='Product description' optional />
                  )}
                />
              </>
            )}
          </FieldZone>
        </div>
        <div className='lg:max-2xl:w-[320px] lg:max-2xl:shrink-0'>
          <FieldZone title='Images'>
            <ImageField
              label='Product image'
              dropzoneId='product-image-dropzone'
              squareCrop
              compact
              existingImage={productImage}
              stagedFile={skuProductImage}
              onStage={(file) => setValue('skuProductImage', file)}
              onRemoveExisting={(id) => {
                setValue('deletedProductImageId', id);
                setValue('productImage', null);
              }}
            />
            <ImageField
              label='Product barcode image'
              dropzoneId='product-barcode-dropzone'
              compact
              existingImage={productBarcodeImage}
              stagedFile={skuProductBarcodeImage}
              onStage={(file) => setValue('skuProductBarcodeImage', file)}
              onRemoveExisting={(id) => {
                setValue('deletedProductBarcodeImageId', id);
                setValue('productBarcodeImage', null);
              }}
            />
          </FieldZone>
        </div>
      </div>
    </section>
  );
}
